import {TitleSelectionValue} from './title-selection-component.domain';
import {Address2ComponentValue} from './address2-component.domain';
import {PropertyOwnerWithAddressValue} from './property-owner-with-address-component.domain';
import {YesNoValue} from './yes-no-component.domain';
import {IdentificationComponentValue} from './identification-component.domain';
import {UploadFileValue} from './file-upload-component.domain';
import {Moment} from 'moment';

export type CountryOfCitizenshipValue = {name: string, code: string} | null;

// Unified value emitted by the shared <individual-form> component. Gender and per-person
// guarantor selection are intentionally absent (guarantor is selected on the KYB screen).
export type IndividualFormValue = {
  title: TitleSelectionValue,
  firstName: string,
  middleName: string,
  lastName: string,
  dob: Moment,
  hasAlias: boolean,
  aliasNames: string | null,
  countryOfCitizenship: CountryOfCitizenshipValue,
  email: string,
  mobileNumber: string,
  residentialAddress: Address2ComponentValue,
  propertyOwner: PropertyOwnerWithAddressValue | YesNoValue,
  identification: IdentificationComponentValue,
  // in-memory files only; must be uploaded by the page on submission and never serialised into payloads
  idDocument: UploadFileValue,
} | null;

// Australia is listed first for convenience (most common selection); there is no default.
export const CountryOfCitizenshipOptions: Exclude<CountryOfCitizenshipValue, null>[] = [
  {name: 'Australia', code: 'AU'},
  {name: 'Afghanistan', code: 'AF'},
  {name: 'Albania', code: 'AL'},
  {name: 'Algeria', code: 'DZ'},
  {name: 'Andorra', code: 'AD'},
  {name: 'Angola', code: 'AO'},
  {name: 'Antigua and Barbuda', code: 'AG'},
  {name: 'Argentina', code: 'AR'},
  {name: 'Armenia', code: 'AM'},
  {name: 'Austria', code: 'AT'},
  {name: 'Azerbaijan', code: 'AZ'},
  {name: 'Bahamas', code: 'BS'},
  {name: 'Bahrain', code: 'BH'},
  {name: 'Bangladesh', code: 'BD'},
  {name: 'Barbados', code: 'BB'},
  {name: 'Belarus', code: 'BY'},
  {name: 'Belgium', code: 'BE'},
  {name: 'Belize', code: 'BZ'},
  {name: 'Benin', code: 'BJ'},
  {name: 'Bhutan', code: 'BT'},
  {name: 'Bolivia', code: 'BO'},
  {name: 'Bosnia and Herzegovina', code: 'BA'},
  {name: 'Botswana', code: 'BW'},
  {name: 'Brazil', code: 'BR'},
  {name: 'Brunei', code: 'BN'},
  {name: 'Bulgaria', code: 'BG'},
  {name: 'Burkina Faso', code: 'BF'},
  {name: 'Burundi', code: 'BI'},
  {name: 'Cabo Verde', code: 'CV'},
  {name: 'Cambodia', code: 'KH'},
  {name: 'Cameroon', code: 'CM'},
  {name: 'Canada', code: 'CA'},
  {name: 'Central African Republic', code: 'CF'},
  {name: 'Chad', code: 'TD'},
  {name: 'Chile', code: 'CL'},
  {name: 'China', code: 'CN'},
  {name: 'Colombia', code: 'CO'},
  {name: 'Comoros', code: 'KM'},
  {name: 'Congo (Democratic Republic)', code: 'CD'},
  {name: 'Congo (Republic)', code: 'CG'},
  {name: 'Costa Rica', code: 'CR'},
  {name: 'Croatia', code: 'HR'},
  {name: 'Cuba', code: 'CU'},
  {name: 'Cyprus', code: 'CY'},
  {name: 'Czechia', code: 'CZ'},
  {name: 'Denmark', code: 'DK'},
  {name: 'Djibouti', code: 'DJ'},
  {name: 'Dominica', code: 'DM'},
  {name: 'Dominican Republic', code: 'DO'},
  {name: 'Ecuador', code: 'EC'},
  {name: 'Egypt', code: 'EG'},
  {name: 'El Salvador', code: 'SV'},
  {name: 'Equatorial Guinea', code: 'GQ'},
  {name: 'Eritrea', code: 'ER'},
  {name: 'Estonia', code: 'EE'},
  {name: 'Eswatini', code: 'SZ'},
  {name: 'Ethiopia', code: 'ET'},
  {name: 'Fiji', code: 'FJ'},
  {name: 'Finland', code: 'FI'},
  {name: 'France', code: 'FR'},
  {name: 'Gabon', code: 'GA'},
  {name: 'Gambia', code: 'GM'},
  {name: 'Georgia', code: 'GE'},
  {name: 'Germany', code: 'DE'},
  {name: 'Ghana', code: 'GH'},
  {name: 'Greece', code: 'GR'},
  {name: 'Grenada', code: 'GD'},
  {name: 'Guatemala', code: 'GT'},
  {name: 'Guinea', code: 'GN'},
  {name: 'Guinea-Bissau', code: 'GW'},
  {name: 'Guyana', code: 'GY'},
  {name: 'Haiti', code: 'HT'},
  {name: 'Honduras', code: 'HN'},
  {name: 'Hungary', code: 'HU'},
  {name: 'Iceland', code: 'IS'},
  {name: 'India', code: 'IN'},
  {name: 'Indonesia', code: 'ID'},
  {name: 'Iran', code: 'IR'},
  {name: 'Iraq', code: 'IQ'},
  {name: 'Ireland', code: 'IE'},
  {name: 'Israel', code: 'IL'},
  {name: 'Italy', code: 'IT'},
  {name: 'Ivory Coast', code: 'CI'},
  {name: 'Jamaica', code: 'JM'},
  {name: 'Japan', code: 'JP'},
  {name: 'Jordan', code: 'JO'},
  {name: 'Kazakhstan', code: 'KZ'},
  {name: 'Kenya', code: 'KE'},
  {name: 'Kiribati', code: 'KI'},
  {name: 'Kosovo', code: 'XK'},
  {name: 'Kuwait', code: 'KW'},
  {name: 'Kyrgyzstan', code: 'KG'},
  {name: 'Laos', code: 'LA'},
  {name: 'Latvia', code: 'LV'},
  {name: 'Lebanon', code: 'LB'},
  {name: 'Lesotho', code: 'LS'},
  {name: 'Liberia', code: 'LR'},
  {name: 'Libya', code: 'LY'},
  {name: 'Liechtenstein', code: 'LI'},
  {name: 'Lithuania', code: 'LT'},
  {name: 'Luxembourg', code: 'LU'},
  {name: 'Madagascar', code: 'MG'},
  {name: 'Malawi', code: 'MW'},
  {name: 'Malaysia', code: 'MY'},
  {name: 'Maldives', code: 'MV'},
  {name: 'Mali', code: 'ML'},
  {name: 'Malta', code: 'MT'},
  {name: 'Marshall Islands', code: 'MH'},
  {name: 'Mauritania', code: 'MR'},
  {name: 'Mauritius', code: 'MU'},
  {name: 'Mexico', code: 'MX'},
  {name: 'Micronesia', code: 'FM'},
  {name: 'Moldova', code: 'MD'},
  {name: 'Monaco', code: 'MC'},
  {name: 'Mongolia', code: 'MN'},
  {name: 'Montenegro', code: 'ME'},
  {name: 'Morocco', code: 'MA'},
  {name: 'Mozambique', code: 'MZ'},
  {name: 'Myanmar', code: 'MM'},
  {name: 'Namibia', code: 'NA'},
  {name: 'Nauru', code: 'NR'},
  {name: 'Nepal', code: 'NP'},
  {name: 'Netherlands', code: 'NL'},
  {name: 'New Zealand', code: 'NZ'},
  {name: 'Nicaragua', code: 'NI'},
  {name: 'Niger', code: 'NE'},
  {name: 'Nigeria', code: 'NG'},
  {name: 'North Korea', code: 'KP'},
  {name: 'North Macedonia', code: 'MK'},
  {name: 'Norway', code: 'NO'},
  {name: 'Oman', code: 'OM'},
  {name: 'Pakistan', code: 'PK'},
  {name: 'Palau', code: 'PW'},
  {name: 'Palestine', code: 'PS'},
  {name: 'Panama', code: 'PA'},
  {name: 'Papua New Guinea', code: 'PG'},
  {name: 'Paraguay', code: 'PY'},
  {name: 'Peru', code: 'PE'},
  {name: 'Philippines', code: 'PH'},
  {name: 'Poland', code: 'PL'},
  {name: 'Portugal', code: 'PT'},
  {name: 'Qatar', code: 'QA'},
  {name: 'Romania', code: 'RO'},
  {name: 'Russia', code: 'RU'},
  {name: 'Rwanda', code: 'RW'},
  {name: 'Saint Kitts and Nevis', code: 'KN'},
  {name: 'Saint Lucia', code: 'LC'},
  {name: 'Saint Vincent and the Grenadines', code: 'VC'},
  {name: 'Samoa', code: 'WS'},
  {name: 'San Marino', code: 'SM'},
  {name: 'Sao Tome and Principe', code: 'ST'},
  {name: 'Saudi Arabia', code: 'SA'},
  {name: 'Senegal', code: 'SN'},
  {name: 'Serbia', code: 'RS'},
  {name: 'Seychelles', code: 'SC'},
  {name: 'Sierra Leone', code: 'SL'},
  {name: 'Singapore', code: 'SG'},
  {name: 'Slovakia', code: 'SK'},
  {name: 'Slovenia', code: 'SI'},
  {name: 'Solomon Islands', code: 'SB'},
  {name: 'Somalia', code: 'SO'},
  {name: 'South Africa', code: 'ZA'},
  {name: 'South Korea', code: 'KR'},
  {name: 'South Sudan', code: 'SS'},
  {name: 'Spain', code: 'ES'},
  {name: 'Sri Lanka', code: 'LK'},
  {name: 'Sudan', code: 'SD'},
  {name: 'Suriname', code: 'SR'},
  {name: 'Sweden', code: 'SE'},
  {name: 'Switzerland', code: 'CH'},
  {name: 'Syria', code: 'SY'},
  {name: 'Taiwan', code: 'TW'},
  {name: 'Tajikistan', code: 'TJ'},
  {name: 'Tanzania', code: 'TZ'},
  {name: 'Thailand', code: 'TH'},
  {name: 'Timor-Leste', code: 'TL'},
  {name: 'Togo', code: 'TG'},
  {name: 'Tonga', code: 'TO'},
  {name: 'Trinidad and Tobago', code: 'TT'},
  {name: 'Tunisia', code: 'TN'},
  {name: 'Turkey', code: 'TR'},
  {name: 'Turkmenistan', code: 'TM'},
  {name: 'Tuvalu', code: 'TV'},
  {name: 'Uganda', code: 'UG'},
  {name: 'Ukraine', code: 'UA'},
  {name: 'United Arab Emirates', code: 'AE'},
  {name: 'United Kingdom', code: 'GB'},
  {name: 'United States', code: 'US'},
  {name: 'Uruguay', code: 'UY'},
  {name: 'Uzbekistan', code: 'UZ'},
  {name: 'Vanuatu', code: 'VU'},
  {name: 'Vatican City', code: 'VA'},
  {name: 'Venezuela', code: 'VE'},
  {name: 'Vietnam', code: 'VN'},
  {name: 'Yemen', code: 'YE'},
  {name: 'Zambia', code: 'ZM'},
  {name: 'Zimbabwe', code: 'ZW'},
];

export const countryOfCitizenshipByName = (name?: string | null): CountryOfCitizenshipValue => {
  if (!name) {
    return null;
  }
  return CountryOfCitizenshipOptions.find(c => c.name.toLowerCase() === name.toLowerCase()) ?? null;
};

// The residency warning is only for a personal guarantor or a guarantor who also signs.
// Signer, trustee, partner and other roles do not get it.
export const roleNeedsCitizenshipResidencyAlert = (role: string | null | undefined): boolean =>
  role === 'Guarantor' || role === 'GuarantorSigner';

// Structural subset shared by the flat role values (director / trustee / partner / member /
// guarantor / sole-trader) so they can be converted to and from IndividualFormValue.
export interface IndividualFormFlatFields {
  title: TitleSelectionValue;
  firstName: string;
  middleName?: string;
  lastName: string;
  dob: Moment;
  hasAlias?: boolean;
  aliasNames?: string | null;
  countryOfCitizenship?: CountryOfCitizenshipValue;
  email: string;
  mobileNumber?: string;
  mobile?: string; // sole-trader historically uses `mobile` instead of `mobileNumber`
  residentialAddress: Address2ComponentValue;
  propertyOwner: PropertyOwnerWithAddressValue | YesNoValue;
  identification?: IdentificationComponentValue;
  idDocument?: UploadFileValue;
}

export const toIndividualFormValue = (flat?: Partial<IndividualFormFlatFields> | null): IndividualFormValue => {
  if (!flat) {
    return null;
  }
  return {
    title: flat.title ?? null,
    firstName: flat.firstName ?? '',
    middleName: flat.middleName ?? '',
    lastName: flat.lastName ?? '',
    dob: flat.dob!,
    hasAlias: flat.hasAlias ?? false,
    aliasNames: flat.aliasNames ?? null,
    countryOfCitizenship: flat.countryOfCitizenship ?? null,
    email: flat.email ?? '',
    mobileNumber: flat.mobileNumber ?? flat.mobile ?? '',
    residentialAddress: flat.residentialAddress ?? null,
    propertyOwner: flat.propertyOwner ?? null,
    identification: flat.identification ?? null,
    idDocument: flat.idDocument ?? null,
  };
};

export const fromIndividualFormValue = (v: IndividualFormValue): Omit<IndividualFormFlatFields, 'mobile'> | null => {
  if (!v) {
    return null;
  }
  return {
    title: v.title,
    firstName: v.firstName,
    middleName: v.middleName,
    lastName: v.lastName,
    dob: v.dob,
    hasAlias: v.hasAlias,
    aliasNames: v.aliasNames,
    countryOfCitizenship: v.countryOfCitizenship,
    email: v.email,
    mobileNumber: v.mobileNumber,
    residentialAddress: v.residentialAddress,
    propertyOwner: v.propertyOwner,
    identification: v.identification,
    idDocument: v.idDocument,
  };
};
